/**
 * SIL-2141 - Market Pulse on the customer Overview (design 2a / 2b).
 *
 * Three modules fed by ONE server-ranked payload (`GET /api/client/dashboard/
 * market-pulse`): Recommended for your fleet, Deepest discounts, and the
 * exchange summary line. All ranking and price projection happen server-side;
 * this file only renders. Unverified buyers get null FMV figures, rendered as
 * "Below market" rather than a percent.
 */
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { Store } from 'lucide-react';
import { Card } from '@siliconx/shared/ui/Card';
import { SectionLabel } from '@siliconx/shared/ui/SectionLabel';
import { ConditionBadge } from '@siliconx/shared/ui/ConditionBadge';
import { ErrorState } from '@siliconx/shared/ui/state';
import { listingCardPctVsFmv } from '@siliconx/shared/ui/buyer-listing/copy/marketplace';
import type { MarketPulse as MarketPulseData, MarketPulseReason } from '@siliconx/shared';
import type { MarketplaceListing } from '@siliconx/shared/dto/marketplace';
import { api, throwIfNotOk } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import { formatUsd } from '../../lib/format';
import { ListingCard } from '../exchange/ListingCard';

/** Why-line under a recommended card; `new` has none (it renders a tag). */
export function buildWhyLine(reason: MarketPulseReason): string | null {
  switch (reason.kind) {
    case 'same_sku':
      return `Same SKU as your ${reason.fleetUnits} ${reason.fleetModelName}`;
    case 'same_family':
      return `Same family as your ${reason.fleetUnits} ${reason.fleetModelName}`;
    case 'same_generation':
      return `Same generation (${reason.generation}) as your ${reason.fleetUnits} ${reason.fleetModelName}`;
    case 'new':
      return null;
  }
}

export function useMarketPulse() {
  return useQuery<MarketPulseData>({
    queryKey: qk.dashboard.marketPulse(),
    queryFn: async () => {
      const res = await api.api.client.dashboard['market-pulse'].$get();
      await throwIfNotOk(res);
      return (await res.json()) as unknown as MarketPulseData;
    },
    staleTime: 60_000,
  });
}

function PulseSkeleton() {
  return (
    <div className="space-y-4" aria-busy="true" data-testid="market-pulse-loading">
      <div className="grid gap-4 md:grid-cols-3">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-64 animate-pulse rounded-lg bg-muted" />
        ))}
      </div>
      <div className="h-24 animate-pulse rounded-lg bg-muted" />
    </div>
  );
}

function RecommendedModule({
  recommended,
  hasFleet,
}: {
  recommended: MarketPulseData['recommended'];
  hasFleet: boolean;
}) {
  if (recommended.length === 0) return null;
  const allNew = recommended.every((r) => r.reason.kind === 'new');
  return (
    <section className="space-y-3" data-testid="market-pulse-recommended">
      <div className="flex items-baseline justify-between">
        <SectionLabel>{allNew ? 'New on the exchange' : 'Recommended for your fleet'}</SectionLabel>
        <Link to="/marketplace" className="text-sm font-medium text-brand-600 hover:underline">
          View all
        </Link>
      </div>
      {allNew && hasFleet && (
        <p className="text-sm text-muted-foreground">No listings match your fleet right now.</p>
      )}
      <div className="grid gap-4 md:grid-cols-3">
        {recommended.map(({ listing, reason }) => {
          const why = buildWhyLine(reason);
          return (
            <div key={listing.id} className="flex flex-col gap-2">
              <ListingCard listing={listing} />
              {why ? (
                <p className="text-xs text-muted-foreground">{why}</p>
              ) : (
                <span className="w-fit rounded-full border border-border px-2 py-0.5 text-xs font-medium text-muted-foreground">
                  New
                </span>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function discountTitle(listing: MarketplaceListing): string {
  return listing.displayTitle ?? listing.assetClassId ?? 'Listing';
}

function DiscountRow({ item }: { item: MarketPulseData['discounts'][number] }) {
  const { listing, fmvMedianUsd, pctVsFmv } = item;
  const verified = pctVsFmv !== null && fmvMedianUsd !== null;
  // pctVsFmv and the FMV median are per-unit; show the per-unit price beside
  // them. If it is missing, show the total with an explicit label and no FMV.
  const perUnitUsd = listing.pricing.tier === 'verified' ? listing.pricing.pricePerGpuUsd : null;
  const totalOnlyUsd =
    listing.pricing.tier === 'verified' && perUnitUsd == null
      ? listing.pricing.totalUsd
      : listing.pricing.tier === 'ask'
        ? listing.pricing.priceUsd
        : null;
  const priceUsd = perUnitUsd ?? totalOnlyUsd;
  const showFmv = verified && perUnitUsd != null;
  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-1 py-3" data-testid="market-pulse-discount">
      <span className="w-28 shrink-0 text-sm font-semibold text-status-green">
        {verified ? listingCardPctVsFmv(pctVsFmv) : 'Below market'}
      </span>
      <Link
        to="/listings/$listingId"
        params={{ listingId: listing.id }}
        className="min-w-0 flex-1 truncate text-sm font-medium text-foreground hover:underline"
      >
        {discountTitle(listing)}
      </Link>
      <ConditionBadge value={listing.conditionBand} />
      {priceUsd !== null && (
        <span className="font-num text-sm text-foreground">
          {formatUsd(priceUsd)}
          {listing.pricing.tier === 'verified' && perUnitUsd == null && <span className="ml-1 text-xs text-muted-foreground">total</span>}
        </span>
      )}
      {showFmv && (
        <span className="flex items-baseline gap-1 text-xs text-muted-foreground">
          <span className="font-num">{formatUsd(fmvMedianUsd)}</span>
          <span>FMV</span>
        </span>
      )}
    </li>
  );
}

function DiscountsModule({ discounts }: { discounts: MarketPulseData['discounts'] }) {
  if (discounts.length === 0) return null;
  return (
    <section className="space-y-2" data-testid="market-pulse-discounts">
      <div className="flex items-center justify-between">
        <SectionLabel>Deepest discounts</SectionLabel>
        <Link to="/marketplace" className="text-sm font-medium text-brand-600 hover:underline">
          See all
        </Link>
      </div>
      <Card padding="md">
        <ul className="divide-y divide-border">
          {discounts.map((d) => (
            <DiscountRow key={d.listing.id} item={d} />
          ))}
        </ul>
      </Card>
    </section>
  );
}

function ExchangeModule({ exchange }: { exchange: MarketPulseData['exchange'] }) {
  const empty = exchange.activeListings === 0;
  return (
    <Card padding="md" className="flex flex-wrap items-center justify-between gap-3" data-testid="market-pulse-exchange">
      <div className="flex items-center gap-3">
        <Store className="size-5 text-muted-foreground" />
        <div className="flex flex-col">
          <span className="text-sm font-semibold text-foreground">
            {empty ? 'The exchange has no active listings yet' : 'Browse the full exchange'}
          </span>
          {!empty && (
            <span className="text-xs text-muted-foreground">
              {`${exchange.activeListings.toLocaleString('en-US')} listings - ${exchange.units.toLocaleString('en-US')} units - ${exchange.skus.toLocaleString('en-US')} SKUs`}
            </span>
          )}
        </div>
      </div>
      <Link
        to="/marketplace"
        className="rounded-md bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
      >
        View all listings
      </Link>
    </Card>
  );
}

export function MarketPulse({ hasFleet }: { hasFleet: boolean }) {
  const q = useMarketPulse();
  if (q.isLoading) return <PulseSkeleton />;
  if (q.error || !q.data) {
    return (
      <ErrorState title="Couldn't load market pulse." error={q.error} onRetry={() => void q.refetch()} />
    );
  }
  const { recommended, discounts, exchange } = q.data;
  return (
    <div className="space-y-6" data-testid="market-pulse">
      <RecommendedModule recommended={recommended} hasFleet={hasFleet} />
      <DiscountsModule discounts={discounts} />
      <ExchangeModule exchange={exchange} />
    </div>
  );
}
